import React from "react";
import {
  Button,
  ButtonGroup,
  ClickAwayListener,
  Grid,
  Grow,
  ListItemIcon,
  MenuItem,
  MenuList,
  Paper,
  Popper
} from "@material-ui/core";
import { useObserver } from "mobx-react";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import { CommentBoxStore } from "./CommentBoxStore";
import { Props } from "./CommentBox";
import locale from "@bonliva-crm/locale";
import CommentIcon from "@material-ui/icons/Comment";
import PersonPinIcon from "@material-ui/icons/PersonPin";
import SmsIcon from "@material-ui/icons/Sms";
import ChatIcon from "@material-ui/icons/Chat";
import EmailIcon from "@material-ui/icons/Email";

type CommentButtonProps = {
  store: CommentBoxStore;
  commentProps: Props<any>;
  ref: any;
};

export const CommentButton = React.forwardRef((props: CommentButtonProps, ref) => {
  const {
    store,
    commentProps: { withButtons, smsDisabled, chatDisabled }
  } = props;

  const { dropdownBtnIndex } = store;

  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLDivElement>(null);

  const handleMenuItemClick = (
    event: React.MouseEvent<HTMLLIElement, MouseEvent>,
    index: number
  ) => {
    store.dropdownBtnIndex = index;
    setOpen(false);
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: React.MouseEvent<Document, MouseEvent>) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }

    setOpen(false);
  };

  return useObserver(() => {
    const { valid, disabled, isSubmitting, dirty } = store;
    const actionDisabled = disabled || isSubmitting || !valid || !dirty;
    const buttons = [];

    if (withButtons.includes("comment")) {
      buttons.push({
        id: "commentOption",
        disabled: actionDisabled,
        label: locale.DoComment,
        leftIcon: <CommentIcon />,
        onClick: store.onComment
      });
    }

    // if (withButtons.includes("reminder")) {
    //   buttons.push({
    //     id: "reminderOption",
    //     label: locale.Reminder,
    //     leftIcon: <CalendarIcon />,
    //     disabled: actionDisabled,
    //     onClick: store.onReminder
    //   });
    // }

    if (withButtons.includes("metInPerson")) {
      buttons.push({
        id: "metInPersonOption",
        label: locale.MetInPerson,
        leftIcon: <PersonPinIcon />,
        disabled: actionDisabled,
        onClick: store.onMetInPerson
      });
    }

    if (withButtons.includes("SMS")) {
      buttons.push({
        id: "smsOption",
        label: locale.SendSMS,
        leftIcon: <SmsIcon />,
        disabled: smsDisabled || actionDisabled,
        optionDisabled: smsDisabled,
        title: smsDisabled ? locale.NoCellphoneNumberRegistered : "",
        onClick: store.onSMS
      });
    }

    if (withButtons.includes("chat")) {
      buttons.push({
        id: "chatOption",
        label: locale.Chat,
        disabled: chatDisabled || actionDisabled,
        optionDisabled: chatDisabled,
        leftIcon: <ChatIcon />,
        onClick: store.onChat
      });
    }

    if (withButtons.includes("mail")) {
      buttons.push({
        id: "mailOption",
        label: locale.SendMail,
        leftIcon: <EmailIcon />,
        title: "Not implement",
        onClick: store.onEmail
      });
    }
    return (
      <Grid container direction="column" alignItems="center">
        <Grid item xs={12}>
          <ButtonGroup
            variant="contained"
            color="primary"
            ref={anchorRef}
            aria-label="split button"
          >
            <Button
              disabled={disabled || buttons[dropdownBtnIndex].disabled}
              color="default"
              onClick={buttons[dropdownBtnIndex].onClick}
            >
              {buttons[dropdownBtnIndex].label}
            </Button>
            <Button
              color="default"
              size="small"
              aria-controls={open ? "split-button-menu" : undefined}
              aria-expanded={open ? "true" : undefined}
              aria-haspopup="menu"
              onClick={handleToggle}
            >
              <ArrowDropDownIcon />
            </Button>
          </ButtonGroup>
          <Popper
            open={open}
            anchorEl={anchorRef.current}
            style={{
              zIndex: 1
            }}
            role={undefined}
            transition
            disablePortal
          >
            {({ TransitionProps, placement }) => (
              <Grow
                {...TransitionProps}
                style={{
                  transformOrigin: placement === "bottom" ? "center top" : "center bottom"
                }}
              >
                <Paper>
                  <ClickAwayListener onClickAway={handleClose}>
                    <MenuList id="split-button-menu">
                      {buttons.map((option, index) => (
                        <MenuItem
                          key={option.id}
                          id={option.id}
                          disabled={option.optionDisabled}
                          selected={index === dropdownBtnIndex}
                          onClick={(event) => handleMenuItemClick(event, index)}
                        >
                          {option.leftIcon && (
                            <ListItemIcon>{option.leftIcon}</ListItemIcon>
                          )}
                          {option.label}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </ClickAwayListener>
                </Paper>
              </Grow>
            )}
          </Popper>
        </Grid>
      </Grid>
    );
  });
});
